import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import MessageInbox from './components/MessageInbox';
import ConversationView from './components/ConversationView';
import NewMessageModal from './components/NewMessageModal';
import { messagingService } from '../../lib/services';

const MessagingPage = () => {
  const { userProfile } = useAuth();
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [isNewMessageOpen, setIsNewMessageOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    loadThreads();
    setupRealtimeSubscription();

    return () => {
      if (subscription) {
        subscription?.unsubscribe();
      }
    };
  }, []);

  const loadThreads = async () => {
    setLoading(true);
    const { data, error } = await messagingService?.getUserThreads();
    if (!error && data) {
      setThreads(data);
    }
    setLoading(false);
  };

  const setupRealtimeSubscription = () => {
    const sub = messagingService?.subscribeToUserThreads((newMessage) => {
      // Reload threads when new message arrives
      loadThreads();
    });
    setSubscription(sub);
  };

  const handleThreadSelect = (thread) => {
    setSelectedThread(thread);
    messagingService?.markThreadAsRead(thread?.id);
  };

  const handleNewThread = async (subject, participantIds) => {
    const { data, error } = await messagingService?.createThread(subject, participantIds);
    if (!error && data) {
      await loadThreads();
      setSelectedThread(data);
      setIsNewMessageOpen(false);
    }
    return { data, error };
  };

  const handleSendMessage = async (content) => {
    if (!selectedThread) return;
    const { data, error } = await messagingService?.sendMessage(selectedThread?.id, content);
    if (!error) {
      // Message will be added via realtime subscription
    }
    return { data, error };
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <DashboardNavigation />
      </div>

      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                Messages
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Communicate with trainers, staff, and members across all gym locations
              </p>
            </div>
            <Button
              onClick={() => setIsNewMessageOpen(true)}
              iconName="Plus"
              iconPosition="left"
            >
              New Message
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <MessageInbox
                threads={threads}
                selectedThread={selectedThread}
                onThreadSelect={handleThreadSelect}
                loading={loading}
                currentUserId={userProfile?.id}
              />
            </div>

            <div className="lg:col-span-2">
              {selectedThread ? (
                <ConversationView
                  thread={selectedThread}
                  onSendMessage={handleSendMessage}
                  currentUserId={userProfile?.id}
                />
              ) : (
                <div className="bg-card rounded-xl p-12 shadow-md border border-border h-full flex flex-col items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
                    <Icon name="MessageSquare" size={40} color="var(--color-muted-foreground)" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    No Conversation Selected
                  </h3>
                  <p className="text-sm text-muted-foreground text-center max-w-sm">
                    Select a conversation from the inbox or start a new message to begin chatting
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {isNewMessageOpen && (
        <NewMessageModal
          onClose={() => setIsNewMessageOpen(false)}
          onCreateThread={handleNewThread}
        />
      )}
    </div>
  );
};

export default MessagingPage;
